import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, axiosClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import { Button, Card, Input, Select, Modal, Table, Skeleton, EmptyState, useToast } from '../../design-system';
import '../styles/Permissions.css';

// Only Partner+ (level 4) may edit — the auth-service enforces the same gate
// on its role/permission write routes, so this mirrors the server, not a
// frontend-only fence.
const EDIT_MIN_LEVEL = 4;

// Mirror of the Permission model's category enum (auth-service
// src/data/permissionCatalog.js). Keep in step when the catalog changes.
// 'nav' leads: it controls which sidebar entries a role can see at all, which
// is the first thing an admin reaches for when shaping what a new hire gets.
const CATEGORIES = [
  'nav', 'users', 'roles', 'permissions', 'tasks', 'clients', 'credentials', 'documents',
  'notices', 'requests', 'compliance', 'dsc', 'auditors', 'expirables', 'services',
  'portals', 'reports', 'auditlogs', 'todos', 'gst', 'payroll',
];

// The five standard roles: name/level fixed, not deletable (server-enforced
// too). Custom roles beyond these are fully editable.
const CORE_ROLES = ['Admin', 'Partner', 'Manager', 'Senior Associate', 'Associate'];

// What each rank means across the apps — shown when picking a custom role's level.
const LEVEL_LABELS = {
  5: 'Admin-equivalent (full access)',
  4: 'Partner-equivalent (firm cockpit)',
  3: 'Manager-equivalent (approvals, client writes)',
  2: 'Senior Associate-equivalent',
  1: 'Associate-equivalent',
  0: 'Below Associate (trainee/intern)',
};

const emptyPermForm = { key: '', description: '', category: 'tasks' };
const emptyRoleForm = { name: '', level: 1, description: '' };

// Working copy of every role's permission set for the staged matrix editor:
// { [roleId]: Set<permissionId> }. Rebuilt from the server whenever the
// baseline reloads.
const buildDraft = (rolesList) => {
  const d = {};
  for (const r of rolesList) {
    d[r._id] = new Set((r.permissions || []).map((p) => String(typeof p === 'object' ? p._id : p)));
  }
  return d;
};

// A checkbox that can render the "some but not all" state — used for the
// per-category bulk toggles in each role column.
function TriStateCheckbox({ checked, indeterminate, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !checked && indeterminate;
  }, [checked, indeterminate]);
  return <input ref={ref} type="checkbox" className="permission-toggle" checked={checked} {...rest} />;
}

function OrgAdminPermissions() {
  const { user } = useAuth();
  const toast = useToast();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [userCounts, setUserCounts] = useState(null); // roleId -> count of firm users; null = unavailable
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('active'); // 'active' | 'deleted'
  const [deletedPerms, setDeletedPerms] = useState([]);
  const [deletedRoles, setDeletedRoles] = useState([]);
  const [restoringId, setRestoringId] = useState(null);
  // Matrix edits are staged here and only persisted on Save (see buildDraft).
  const [draft, setDraft] = useState({});
  const [savingPerms, setSavingPerms] = useState(false);

  // Permission modals: { mode: 'add' | 'edit' | 'delete', permission? }
  // Role modals: { mode: 'role-add' } | { mode: 'role-edit' | 'role-delete', role }
  const [modal, setModal] = useState(null);
  const [permForm, setPermForm] = useState(emptyPermForm);
  const [roleForm, setRoleForm] = useState(emptyRoleForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const canEdit = (user?.role?.level ?? 0) >= EDIT_MIN_LEVEL;

  const fetchAll = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [rolesRes, permsRes, deletedRes, deletedRolesRes] = await Promise.all([
        axiosClient.get('/roles'),
        axiosClient.get('/permissions'),
        axiosClient.get('/permissions?deleted=true'),
        axiosClient.get('/roles?deleted=true'),
      ]);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
      setDeletedPerms(deletedRes.data);
      setDeletedRoles(deletedRolesRes.data);
      setDraft(buildDraft(rolesRes.data));
    } catch (err) {
      setLoadError(true);
      toast.danger(err.response?.data?.error || 'Failed to load roles and permissions');
    } finally {
      setLoading(false);
    }
    // Firm-local headcount per role — best-effort; the matrix works without it.
    axiosClient
      .get('/users')
      .then((res) => {
        const counts = {};
        for (const u of res.data) {
          const roleId = typeof u.role === 'object' ? u.role?._id : u.role;
          if (roleId) counts[roleId] = (counts[roleId] || 0) + 1;
        }
        setUserCounts(counts);
      })
      .catch(() => setUserCounts(null));
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Super Admin bypasses permission checks entirely (and the backend refuses
  // to modify such a role), so no column for it. Highest level first.
  const visibleRoles = useMemo(
    () => roles.filter((r) => r.name !== 'Super Admin').sort((a, b) => b.level - a.level),
    [roles]
  );

  const filteredByCategory = useMemo(() => {
    const q = search.trim().toLowerCase();
    const groups = {};
    for (const p of permissions) {
      if (q && !p.key.toLowerCase().includes(q) && !p.description?.toLowerCase().includes(q)) continue;
      (groups[p.category] = groups[p.category] || []).push(p);
    }
    for (const list of Object.values(groups)) {
      list.sort((a, b) => a.key.localeCompare(b.key));
    }
    return groups;
  }, [permissions, search]);

  const roleHas = (role, permissionId) =>
    role.permissions?.some((p) => String(typeof p === 'object' ? p._id : p) === String(permissionId));

  const rolePermIds = (role) => (role.permissions || []).map((p) => String(typeof p === 'object' ? p._id : p));

  // ── Staged matrix editing ──────────────────────────────────────────────────
  // Checkboxes bind to `draft`, not the server state; toggling only mutates the
  // draft. Nothing is persisted until Save, so a stray tick costs no request and
  // is free to undo.
  const draftHas = (roleId, permissionId) => !!draft[roleId]?.has(String(permissionId));
  const draftCount = (roleId) => draft[roleId]?.size ?? 0;

  const toggleDraft = (roleId, permissionId) =>
    setDraft((prev) => {
      const set = new Set(prev[roleId] || []);
      const key = String(permissionId);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return { ...prev, [roleId]: set };
    });

  // Grant every visible permission in the category, or revoke them all if the
  // role already holds all of them — one local flip of the draft.
  const toggleCategoryDraft = (roleId, perms) =>
    setDraft((prev) => {
      const set = new Set(prev[roleId] || []);
      const catIds = perms.map((p) => String(p._id));
      const hasAll = catIds.every((id) => set.has(id));
      catIds.forEach((id) => (hasAll ? set.delete(id) : set.add(id)));
      return { ...prev, [roleId]: set };
    });

  // A role is dirty when its draft set differs from what the server holds.
  const roleIsDirty = (role) => {
    const d = draft[role._id];
    if (!d) return false;
    const server = new Set(rolePermIds(role));
    if (d.size !== server.size) return true;
    for (const id of d) if (!server.has(id)) return true;
    return false;
  };

  const dirtyRoles = useMemo(
    () => visibleRoles.filter(roleIsDirty),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleRoles, draft]
  );
  const isDirty = dirtyRoles.length > 0;

  // Throw away staged edits, snapping the matrix back to the server state.
  const discardChanges = () => setDraft(buildDraft(roles));

  // Persist only the changed roles — one bulk PUT each (one audit entry each),
  // instead of a request per checkbox.
  const handleSavePermissions = async () => {
    setSavingPerms(true);
    try {
      const results = await Promise.all(
        dirtyRoles.map((r) => axiosClient.put(`/roles/${r._id}`, { permissionIds: [...(draft[r._id] || [])] }))
      );
      const updatedById = new Map(results.map((res) => [res.data._id, res.data]));
      setRoles((prev) => prev.map((r) => updatedById.get(r._id) || r));
      setDraft((prev) => {
        const next = { ...prev };
        for (const updated of updatedById.values()) {
          next[updated._id] = new Set((updated.permissions || []).map((p) => String(typeof p === 'object' ? p._id : p)));
        }
        return next;
      });
      const n = results.length;
      toast.success(`Saved permissions for ${n} role${n > 1 ? 's' : ''}`);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to save permission changes');
    } finally {
      setSavingPerms(false);
    }
  };

  const openAdd = () => {
    setPermForm(emptyPermForm);
    setFormError('');
    setModal({ mode: 'add' });
  };

  const openEdit = (permission) => {
    setPermForm({ key: permission.key, description: permission.description || '', category: permission.category });
    setFormError('');
    setModal({ mode: 'edit', permission });
  };

  const handleFormSave = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      if (modal.mode === 'add') {
        await axiosClient.post('/permissions', permForm);
        toast.success(`Permission ${permForm.key} created`);
      } else {
        await axiosClient.put(`/permissions/${modal.permission._id}`, {
          description: permForm.description,
          category: permForm.category,
        });
        toast.success(`Permission ${modal.permission.key} updated`);
      }
      setModal(null);
      fetchAll();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save permission');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await axiosClient.delete(`/permissions/${modal.permission._id}`);
      toast.success(`${modal.permission.key} moved to Deleted (revoked from ${res.data?.rolesUpdated ?? 0} roles)`);
      setModal(null);
      fetchAll();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to delete permission');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (permission) => {
    setRestoringId(permission._id);
    try {
      await axiosClient.patch(`/permissions/${permission._id}/restore`);
      toast.success(`${permission.key} restored (re-granted to ${permission.formerRoleIds?.length ?? 0} roles)`);
      fetchAll();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to restore permission');
    } finally {
      setRestoringId(null);
    }
  };

  const handleRestoreRole = async (role) => {
    setRestoringId(role._id);
    try {
      await axiosClient.patch(`/roles/${role._id}/restore`);
      toast.success(`Role "${role.name}" restored`);
      fetchAll();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to restore role');
    } finally {
      setRestoringId(null);
    }
  };

  const openRoleAdd = () => {
    setRoleForm(emptyRoleForm);
    setFormError('');
    setModal({ mode: 'role-add' });
  };

  const openRoleEdit = (role) => {
    setRoleForm({ name: role.name, level: role.level, description: role.description || '' });
    setFormError('');
    setModal({ mode: 'role-edit', role });
  };

  const handleRoleSave = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!roleForm.name.trim()) {
      setFormError('Role name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: roleForm.name.trim(),
        level: Number(roleForm.level),
        description: roleForm.description || undefined,
      };
      if (modal.mode === 'role-add') {
        await axiosClient.post('/roles', payload);
        toast.success(`Role "${payload.name}" created — now tick its permissions`);
      } else {
        await axiosClient.put(`/roles/${modal.role._id}`, payload);
        toast.success(`Role "${payload.name}" updated`);
      }
      setModal(null);
      fetchAll();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleRoleDelete = async () => {
    setSaving(true);
    try {
      await axiosClient.delete(`/roles/${modal.role._id}`);
      toast.success(`Role "${modal.role.name}" moved to Deleted — restorable`);
      setModal(null);
      fetchAll();
    } catch (err) {
      // Most common refusal: users still hold the role (server returns 409).
      toast.danger(err.response?.data?.error || 'Failed to delete role');
      setModal(null);
    } finally {
      setSaving(false);
    }
  };

  const rolesHolding = (permission) => visibleRoles.filter((r) => roleHas(r, permission._id));
  const hasResults = Object.keys(filteredByCategory).length > 0;

  return (
    <OrgAdminLayout title="Permissions" subtitle="What each role is allowed to do">
      <div className="permissions-page">
        <div className="permissions-note">
          {canEdit
            ? 'Tick or untick boxes, then Save changes to apply them. Nothing is sent until you save. Once saved, users pick the change up when their access token refreshes (within 15 minutes) or on next login.'
            : 'Read-only view — only Partners and Admins can change role permissions.'}
        </div>

        <div className="permissions-toolbar">
          <Input
            id="perm-search"
            placeholder="Search permissions (key or description)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search permissions"
          />
          <div className="permissions-view-toggle">
            <Button
              variant={view === 'active' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setView('active')}
            >
              Active
            </Button>
            <Button
              variant={view === 'deleted' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setView('deleted')}
            >
              Deleted{deletedPerms.length + deletedRoles.length > 0 ? ` (${deletedPerms.length + deletedRoles.length})` : ''}
            </Button>
          </div>
          {canEdit && view === 'active' && (
            <>
              {isDirty && (
                <Button variant="ghost" onClick={discardChanges} disabled={savingPerms}>Discard</Button>
              )}
              <Button variant="secondary" onClick={openRoleAdd}>Add Role</Button>
              <Button variant="secondary" onClick={openAdd}>Add Permission</Button>
              <Button
                variant="primary"
                onClick={handleSavePermissions}
                disabled={!isDirty || savingPerms}
                loading={savingPerms}
              >
                {isDirty ? `Save changes (${dirtyRoles.length})` : 'Save changes'}
              </Button>
            </>
          )}
        </div>

        {loading ? (
          <Card className="permissions-section">
            <Skeleton height={220} />
          </Card>
        ) : view === 'deleted' ? (
          deletedPerms.length === 0 && deletedRoles.length === 0 ? (
            <EmptyState icon="🗑️" title="Nothing deleted" description="Deleted roles and permissions land here and can be restored." />
          ) : (
            <>
              {deletedRoles.length > 0 && (
                <Card className="permissions-section">
                  <div className="permissions-deleted-heading">Deleted roles</div>
                  <Table
                    columns={[
                      {
                        key: 'name',
                        header: 'Role',
                        render: (r) => (
                          <div>
                            <span className="permission-key">{r.name}</span>
                            {r.description && <span className="permission-desc">{r.description}</span>}
                          </div>
                        ),
                      },
                      { key: 'level', header: 'Level', render: (r) => `Level ${r.level}` },
                      { key: 'perms', header: 'Permissions', render: (r) => `${r.permissions?.length ?? 0}` },
                      {
                        key: 'deletedAt',
                        header: 'Deleted',
                        render: (r) => (r.deletedAt ? new Date(r.deletedAt).toLocaleDateString() : '—'),
                      },
                      {
                        key: 'actions',
                        header: '',
                        render: (r) =>
                          canEdit ? (
                            <Button
                              variant="secondary"
                              size="sm"
                              loading={restoringId === r._id}
                              onClick={() => handleRestoreRole(r)}
                            >
                              Restore
                            </Button>
                          ) : null,
                      },
                    ]}
                    data={deletedRoles}
                  />
                </Card>
              )}
              {deletedPerms.length > 0 && (
                <Card className="permissions-section">
                  <div className="permissions-deleted-heading">Deleted permissions</div>
                  <Table
                    columns={[
                      {
                        key: 'key',
                        header: 'Permission',
                        render: (p) => (
                          <div>
                            <span className="permission-key">{p.key}</span>
                            {p.description && <span className="permission-desc">{p.description}</span>}
                          </div>
                        ),
                      },
                      { key: 'category', header: 'Category', render: (p) => p.category },
                      {
                        key: 'deletedAt',
                        header: 'Deleted',
                        render: (p) => (p.deletedAt ? new Date(p.deletedAt).toLocaleDateString() : '—'),
                      },
                      {
                        key: 'formerRoles',
                        header: 'Was held by',
                        render: (p) => `${p.formerRoleIds?.length ?? 0} role(s)`,
                      },
                      {
                        key: 'actions',
                        header: '',
                        render: (p) =>
                          canEdit ? (
                            <Button
                              variant="secondary"
                              size="sm"
                              loading={restoringId === p._id}
                              onClick={() => handleRestore(p)}
                            >
                              Restore
                            </Button>
                          ) : null,
                      },
                    ]}
                    data={deletedPerms}
                  />
                </Card>
              )}
            </>
          )
        ) : loadError ? (
          <EmptyState
            icon="⚠️"
            title="Couldn't load permissions"
            description="The auth service didn't respond."
            action={<Button variant="secondary" onClick={fetchAll}>Retry</Button>}
          />
        ) : permissions.length === 0 ? (
          <EmptyState
            icon="🔐"
            title="No permissions defined"
            description="Seed the auth-service (npm run seed) or add the first permission."
            action={canEdit ? <Button variant="primary" onClick={openAdd}>Add Permission</Button> : undefined}
          />
        ) : !hasResults ? (
          <EmptyState icon="🔍" title="No matches" description={`Nothing matches “${search}”.`} />
        ) : (
          <Card className="permissions-section">
            <div className="permissions-matrix-wrap">
              <table className="permissions-matrix">
                <thead>
                  <tr>
                    <th>Permission</th>
                    {visibleRoles.map((r) => {
                      const isCustom = !CORE_ROLES.includes(r.name);
                      return (
                        <th key={r._id} title={r.description || undefined}>
                          <span className="role-name">
                            {r.name}
                            {canEdit && isCustom && (
                              <span className="permission-row-actions role-actions">
                                <button className="permission-action" onClick={() => openRoleEdit(r)} title="Edit role">✎</button>
                                <button className="permission-action danger" onClick={() => setModal({ mode: 'role-delete', role: r })} title="Delete role">🗑</button>
                              </span>
                            )}
                          </span>
                          <span className="role-level">
                            {isCustom ? 'Custom · ' : ''}Level {r.level} · {draftCount(r._id)} perms
                            {roleIsDirty(r) && <span className="role-unsaved"> • unsaved</span>}
                            {userCounts && ` · ${userCounts[r._id] || 0} users`}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(filteredByCategory).map(([category, perms]) => (
                    <CategoryRows
                      key={category}
                      category={category}
                      perms={perms}
                      visibleRoles={visibleRoles}
                      draftHas={draftHas}
                      canEdit={canEdit}
                      disabled={savingPerms}
                      onToggle={toggleDraft}
                      onCategoryToggle={toggleCategoryDraft}
                      onEdit={openEdit}
                      onDelete={(p) => setModal({ mode: 'delete', permission: p })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Add / Edit permission */}
      <Modal
        isOpen={modal?.mode === 'add' || modal?.mode === 'edit'}
        onClose={() => setModal(null)}
        title={modal?.mode === 'add' ? 'Add Permission' : `Edit ${modal?.permission?.key}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModal(null)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleFormSave} loading={saving}>
              {modal?.mode === 'add' ? 'Create' : 'Save Changes'}
            </Button>
          </>
        }
      >
        <form className="permission-form" onSubmit={handleFormSave}>
          {formError && <div className="form-error" role="alert">{formError}</div>}
          <Input
            label="Key *"
            id="perm-key"
            placeholder="e.g. tasks.export"
            value={permForm.key}
            onChange={(e) => setPermForm((f) => ({ ...f, key: e.target.value }))}
            disabled={saving || modal?.mode === 'edit'}
            helperText={modal?.mode === 'edit'
              ? 'Keys are immutable — code checks them by string, renaming would revoke everywhere.'
              : 'Format: module.action, lowercase.'}
          />
          <Input
            label="Description *"
            id="perm-desc"
            placeholder="What does this allow?"
            value={permForm.description}
            onChange={(e) => setPermForm((f) => ({ ...f, description: e.target.value }))}
            disabled={saving}
          />
          <Select
            label="Category *"
            id="perm-category"
            value={permForm.category}
            onChange={(e) => setPermForm((f) => ({ ...f, category: e.target.value }))}
            disabled={saving}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </Select>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={modal?.mode === 'delete'}
        onClose={() => setModal(null)}
        title="Delete permission?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModal(null)} disabled={saving}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete} loading={saving}>Delete</Button>
          </>
        }
      >
        {modal?.mode === 'delete' && (
          <div className="permission-delete-body">
            <p>
              <span className="permission-key">{modal.permission.key}</span> will be removed from the
              catalog <strong>and revoked from every role that holds it</strong>:
            </p>
            {rolesHolding(modal.permission).length ? (
              <ul>
                {rolesHolding(modal.permission).map((r) => (
                  <li key={r._id}>{r.name}</li>
                ))}
              </ul>
            ) : (
              <p>No role currently holds it.</p>
            )}
            <p className="permissions-note">
              This is a soft delete: the permission moves to the Deleted view and can be restored,
              which re-grants it to the roles listed above. While deleted, routes checking this key
              will deny everyone.
            </p>
          </div>
        )}
      </Modal>

      {/* Add / Edit custom role */}
      <Modal
        isOpen={modal?.mode === 'role-add' || modal?.mode === 'role-edit'}
        onClose={() => setModal(null)}
        title={modal?.mode === 'role-add' ? 'Add Custom Role' : `Edit ${modal?.role?.name}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModal(null)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleRoleSave} loading={saving}>
              {modal?.mode === 'role-add' ? 'Create Role' : 'Save Changes'}
            </Button>
          </>
        }
      >
        <form className="permission-form" onSubmit={handleRoleSave}>
          {formError && <div className="form-error" role="alert">{formError}</div>}
          <Input
            label="Role name *"
            id="role-name"
            placeholder="e.g. Article Assistant, Audit Manager"
            value={roleForm.name}
            onChange={(e) => setRoleForm((f) => ({ ...f, name: e.target.value }))}
            disabled={saving}
          />
          <Select
            label="Level *"
            id="role-level"
            value={String(roleForm.level)}
            onChange={(e) => setRoleForm((f) => ({ ...f, level: e.target.value }))}
            disabled={saving}
            helperText="Higher = more power. Every 'minimum level' check in the apps goes by this — you cannot set it above your own level."
          >
            {[5, 4, 3, 2, 1, 0]
              .filter((lvl) => lvl <= (user?.role?.level ?? 0))
              .map((lvl) => (
                <option key={lvl} value={String(lvl)}>
                  {lvl} — {LEVEL_LABELS[lvl]}
                </option>
              ))}
          </Select>
          <Input
            label="Description"
            id="role-desc"
            placeholder="What is this role for?"
            value={roleForm.description}
            onChange={(e) => setRoleForm((f) => ({ ...f, description: e.target.value }))}
            disabled={saving}
          />
          {modal?.mode === 'role-add' && (
            <p className="permissions-note">
              The role starts with no permissions — tick its column in the matrix after creating it.
            </p>
          )}
        </form>
      </Modal>

      {/* Delete role confirmation */}
      <Modal
        isOpen={modal?.mode === 'role-delete'}
        onClose={() => setModal(null)}
        title="Delete role?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModal(null)} disabled={saving}>Cancel</Button>
            <Button variant="danger" onClick={handleRoleDelete} loading={saving}>Delete Role</Button>
          </>
        }
      >
        {modal?.mode === 'role-delete' && (
          <div className="permission-delete-body">
            <p>
              Delete the custom role <strong>{modal.role.name}</strong> (level {modal.role.level})?
            </p>
            {userCounts && (userCounts[modal.role._id] || 0) > 0 ? (
              <p className="permissions-note">
                {userCounts[modal.role._id]} user(s) in your firm hold this role — the server will
                refuse until they are reassigned on the Users page.
              </p>
            ) : (
              <p className="permissions-note">
                No users in your firm hold it. If any user anywhere on the platform still does, the
                server refuses rather than locking them out. This is a soft delete — the role moves
                to the Deleted view and can be restored.
              </p>
            )}
          </div>
        )}
      </Modal>
    </OrgAdminLayout>
  );
}

function CategoryRows({
  category,
  perms,
  visibleRoles,
  draftHas,
  canEdit,
  disabled,
  onToggle,
  onCategoryToggle,
  onEdit,
  onDelete,
}) {
  return (
    <>
      <tr className="permissions-category-row">
        <td>{category}</td>
        {visibleRoles.map((r) => {
          const held = perms.filter((p) => draftHas(r._id, p._id)).length;
          return (
            <td key={r._id}>
              <TriStateCheckbox
                checked={held === perms.length && perms.length > 0}
                indeterminate={held > 0}
                disabled={!canEdit || disabled}
                onChange={() => onCategoryToggle(r._id, perms)}
                aria-label={`All ${category} permissions for ${r.name}`}
              />
            </td>
          );
        })}
      </tr>
      {perms.map((p) => (
        <tr key={p._id}>
          <td>
            <div className="permission-cell">
              <div>
                <span className="permission-key">{p.key}</span>
                {p.description && <span className="permission-desc">{p.description}</span>}
              </div>
              {canEdit && (
                <span className="permission-row-actions">
                  <button className="permission-action" onClick={() => onEdit(p)} title="Edit">✎</button>
                  <button className="permission-action danger" onClick={() => onDelete(p)} title="Delete">🗑</button>
                </span>
              )}
            </div>
          </td>
          {visibleRoles.map((r) => (
            <td key={r._id}>
              <input
                type="checkbox"
                className="permission-toggle"
                checked={draftHas(r._id, p._id)}
                disabled={!canEdit || disabled}
                onChange={() => onToggle(r._id, p._id)}
                aria-label={`${p.key} for ${r.name}`}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export default OrgAdminPermissions;
